/*
 * CONFIDENTIAL & PROPRIETARY
 *
 * Copyright © 2025 AionTrack. All rights reserved.
 *
 * This file contains proprietary algorithms and trade secrets.
 * Unauthorized use, disclosure, or reproduction is strictly prohibited.
 *
 * File: aleParser.ts
 * Contains: ALE (Avid Log Exchange) parsing with custom field normalization and
 * timecode conversion
 * Classification: TRADE SECRET - DO NOT DISTRIBUTE
 */

export interface ParsedClip {
  name: string
  tape_name?: string
  source_timecode?: string
  duration_frames?: number
  fps?: number
  source_file_path?: string
  tags?: string[]
  custom_metadata?: Record<string, any>
}

export interface ALEParseResult {
  success: boolean
  clips: ParsedClip[]
  errors: string[]
  totalRows: number
  parsedRows: number
}

/**
 * Parse ALE (Avid Log Exchange) format files
 *
 * ALE format structure:
 * - Heading section: Contains FIELD_DELIM specification
 * - Column section: Tab-delimited header names
 * - Data section: Tab-delimited clip data
 *
 * Example:
 * ```
 * Heading
 * FIELD_DELIM    TABS
 *
 * Column
 * Name    Tape    Start    Duration    FPS
 *
 * Data
 * CLIP_001    A001C002    01:00:00:00    00:00:05:00    24
 * CLIP_002    A001C003    01:00:10:00    00:00:08:12    24
 * ```
 */
export function parseALEFile(fileContent: string): ALEParseResult {
  const errors: string[] = []
  const clips: ParsedClip[] = []

  try {
    // Split file into lines and trim
    const lines = fileContent.split('\n').map(line => line.trim())

    // Find section markers
    let columnIndex = -1
    let dataIndex = -1

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].toLowerCase()
      if (line === 'column') columnIndex = i
      if (line === 'data') dataIndex = i
    }

    // Validate required sections exist
    if (columnIndex === -1) {
      return {
        success: false,
        clips: [],
        errors: ['Invalid ALE format: Missing "Column" section'],
        totalRows: 0,
        parsedRows: 0,
      }
    }

    if (dataIndex === -1) {
      return {
        success: false,
        clips: [],
        errors: ['Invalid ALE format: Missing "Data" section'],
        totalRows: 0,
        parsedRows: 0,
      }
    }

    // Parse column headers (line after "Column" marker)
    const headerLine = lines[columnIndex + 1]
    if (!headerLine) {
      return {
        success: false,
        clips: [],
        errors: ['Invalid ALE format: Missing column headers'],
        totalRows: 0,
        parsedRows: 0,
      }
    }

    // Split by tabs (ALE standard delimiter)
    const headers = headerLine.split('\t').map(h => h.trim())

    // Normalize common column names to our schema
    const headerMap = normalizeHeaders(headers)

    // Parse data rows (lines after "Data" marker)
    let totalRows = 0
    let parsedRows = 0

    for (let i = dataIndex + 1; i < lines.length; i++) {
      const line = lines[i]

      // Skip empty lines
      if (!line) continue

      totalRows++

      try {
        const values = line.split('\t').map(v => v.trim())

        // Skip if not enough values
        if (values.length === 0 || (values.length === 1 && !values[0])) {
          continue
        }

        const clip: ParsedClip = {
          name: '',
          custom_metadata: {},
        }

        // Map values to clip properties
        // Core fields are mapped to database columns AND also stored in custom_metadata
        // This ensures ALL ALE data is accessible in the metadata section
        for (let j = 0; j < headers.length && j < values.length; j++) {
          const header = headers[j]
          const value = values[j]
          const mappedField = headerMap.get(header)

          // Skip empty values for core fields, but still include them in custom_metadata
          const isEmpty = !value || value.trim() === ''

          // Map to core database fields (for structured queries and database operations)
          if (mappedField === 'name' && !isEmpty) {
            clip.name = value
          } else if (mappedField === 'tape_name' && !isEmpty) {
            clip.tape_name = value
          } else if (mappedField === 'source_timecode' && !isEmpty) {
            clip.source_timecode = value
          } else if (mappedField === 'duration_frames' && !isEmpty) {
            const frames = parseFramesFromDuration(value)
            if (frames !== null) clip.duration_frames = frames
          } else if (mappedField === 'fps' && !isEmpty) {
            const fps = parseFloat(value)
            if (!isNaN(fps) && fps > 0) clip.fps = fps
          } else if (mappedField === 'source_file_path' && !isEmpty) {
            clip.source_file_path = value
          }

          // CRITICAL: Store ALL columns in custom_metadata, including mapped core fields
          // This ensures all ALE data is available in one place for display, filtering, and searching
          // We preserve the original column name exactly as it appears in the ALE
          if (!isEmpty) {
            if (!clip.custom_metadata) clip.custom_metadata = {}
            clip.custom_metadata[header] = value
          }
        }

        // Validate required fields
        if (!clip.name) {
          errors.push(`Row ${totalRows}: Missing clip name`)
          continue
        }

        clips.push(clip)
        parsedRows++
      } catch (error: any) {
        errors.push(`Row ${totalRows}: ${error.message}`)
      }
    }

    return {
      success: clips.length > 0,
      clips,
      errors,
      totalRows,
      parsedRows,
    }
  } catch (error: any) {
    return {
      success: false,
      clips: [],
      errors: [`Failed to parse ALE file: ${error.message}`],
      totalRows: 0,
      parsedRows: 0,
    }
  }
}

/**
 * Normalize column headers to standard field names
 * Supports common variations of column names across different NLEs
 */
function normalizeHeaders(headers: string[]): Map<string, string> {
  const map = new Map<string, string>()

  headers.forEach(header => {
    const lower = header.toLowerCase().trim()

    // Name variations
    if (lower === 'name' || lower === 'clip name' || lower === 'clipname') {
      map.set(header, 'name')
    }
    // Tape name variations
    else if (lower === 'tape' || lower === 'tape name' || lower === 'tapename' || lower === 'reel') {
      map.set(header, 'tape_name')
    }
    // Timecode variations
    else if (lower === 'start' || lower === 'source in' || lower === 'source_in' ||
             lower === 'source timecode' || lower === 'source_timecode' ||
             lower === 'tc in' || lower === 'tc_in') {
      map.set(header, 'source_timecode')
    }
    // Duration variations
    else if (lower === 'duration' || lower === 'length') {
      map.set(header, 'duration_frames')
    }
    // FPS variations
    else if (lower === 'fps' || lower === 'frame rate' || lower === 'framerate') {
      map.set(header, 'fps')
    }
    // Source file variations
    else if (lower === 'source file' || lower === 'source_file' ||
             lower === 'file path' || lower === 'filepath') {
      map.set(header, 'source_file_path')
    }
  })

  return map
}

/**
 * Parse duration from timecode format (HH:MM:SS:FF) to total frames
 * Also handles simple frame count values
 */
function parseFramesFromDuration(duration: string): number | null {
  // If it's just a number, treat it as frames
  const simpleFrames = parseInt(duration, 10)
  if (!isNaN(simpleFrames) && duration === simpleFrames.toString()) {
    return simpleFrames
  }

  // Parse timecode format HH:MM:SS:FF
  const parts = duration.split(':')
  if (parts.length === 4) {
    const [hours, minutes, seconds, frames] = parts.map(p => parseInt(p, 10))

    if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds) && !isNaN(frames)) {
      // Assume 24fps for now - this could be improved by reading FPS from the file
      const fps = 24
      return (hours * 3600 + minutes * 60 + seconds) * fps + frames
    }
  }

  return null
}

/**
 * Validate file is an ALE file
 */
export function isValidALEFile(filename: string): boolean {
  return filename.toLowerCase().endsWith('.ale')
}

/**
 * Read file content as text
 */
export async function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target?.result as string)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}
